import { useMutation } from '@tanstack/react-query';
import api from '../lib/api';
import { isSupportedFileType } from '../constants';

// Types for file upload
export interface FileUploadRequest {
  fileName: string;
  fileType: string;
  fileSize: number;
  /** When provided, the file is uploaded into the conversation workspace so the agent can access it. */
  conversationId?: string;
  /** Workspace session to upload into. Defaults to "main". */
  session?: string;
}

export interface FileUploadResponse {
  // Legacy PUT upload support
  uploadUrl?: string;
  // Presigned POST support
  postUrl?: string; // some backends use postUrl
  url?: string; // some backends use url
  fields?: Record<string, string>;
  // Some backends nest the POST data
  post?: { url?: string; fields?: Record<string, string> };
  postData?: { url?: string; fields?: Record<string, string> };
  presignedPost?: { url?: string; fields?: Record<string, string> };
  data?: { url?: string; fields?: Record<string, string> };
  formData?: Record<string, string>;

  fileUrl: string;
  fileKey: string;
  expiresIn: number;
  /** Workspace-relative path (e.g. "uploads/data.csv") — set when conversationId was provided. */
  workspacePath?: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface FileUploadResult {
  fileUrl: string;
  fileKey: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  /** Workspace-relative path (e.g. "uploads/data.csv") — set when file was uploaded to a workspace. */
  workspacePath?: string;
}

// Hook to get presigned URL for file upload
export const useGetPresignedUrl = () => {
  return useMutation<FileUploadResponse, Error, FileUploadRequest>({
    mutationFn: async (request: FileUploadRequest) => {
      const response = await api.post(
        '/api/core/pleri/files/presigned-url',
        request,
      );
      return response.data;
    },
  });
};

// Hook to upload file to S3 using presigned URL
export const useUploadToS3 = () => {
  return useMutation<
    FileUploadResult,
    Error,
    {
      file: File;
      // One of the following will be provided
      uploadUrl?: string; // legacy PUT
      postUrl?: string; // presigned POST
      url?: string; // presigned POST (alias)
      fields?: Record<string, string>; // presigned POST fields
      fileUrl: string;
      fileKey: string;
      onProgress?: (progress: UploadProgress) => void;
    }
  >({
    mutationFn: async ({
      file,
      uploadUrl,
      postUrl,
      url,
      fields,
      fileUrl,
      fileKey,
      onProgress,
    }) => {
      return new Promise<FileUploadResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const progress: UploadProgress = {
              loaded: event.loaded,
              total: event.total,
              percentage: Math.round((event.loaded / event.total) * 100),
            };
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              fileUrl,
              fileKey,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            });
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed due to network error'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload was aborted'));
        });

        // Decide between legacy PUT and presigned POST
        const resolvedPostUrl = postUrl ?? url;
        if (resolvedPostUrl && fields) {
          // Presigned POST using multipart/form-data
          const formData = new FormData();
          // Add all fields first (order matters for some validators)
          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          // File must be the last field and the field name must be 'file'
          formData.append('file', file);

          xhr.open('POST', resolvedPostUrl);
          // Do NOT set Content-Type; the browser will set correct boundary
          xhr.send(formData);
        } else if (uploadUrl) {
          // Legacy PUT upload
          xhr.open('PUT', uploadUrl);
          if (file.type) {
            xhr.setRequestHeader('Content-Type', file.type);
          }
          xhr.send(file);
        } else {
          reject(
            new Error(
              'Invalid upload request: neither PUT nor POST data provided',
            ),
          );
        }
      });
    },
  });
};

// Combined hook for the complete upload process
export const useFileUpload = () => {
  const getPresignedUrl = useGetPresignedUrl();
  const uploadToS3 = useUploadToS3();

  const normalizePresign = (
    response: FileUploadResponse,
  ): {
    uploadUrl?: string;
    postUrl?: string;
    url?: string;
    fields?: Record<string, string>;
  } => {
    // Generic extractor: recursively scan for an object with both a URL-like
    // string and a fields-like object. This covers all known response shapes
    // while remaining resilient to new ones.
    const URL_KEYS = ['url', 'postUrl', 'action', 'endpoint'] as const;
    const FIELDS_KEYS = [
      'fields',
      'form',
      'formData',
      'Fields',
      'formFields',
      'inputs',
      'formInputs',
      'form_inputs',
    ] as const;

    type UnknownRecord = Record<string, unknown>;

    const extractUrlAndFields = (
      obj: unknown,
    ): { url?: string; fields?: Record<string, string> } | undefined => {
      if (!obj || typeof obj !== 'object') {
        return undefined;
      }

      const record = obj as UnknownRecord;

      let candidateUrl: string | undefined;
      for (const key of URL_KEYS) {
        const value = record[key];
        if (typeof value === 'string') {
          candidateUrl = value;
          break;
        }
      }

      let candidateFields: Record<string, string> | undefined;
      for (const key of FIELDS_KEYS) {
        const value = record[key];
        if (value && typeof value === 'object') {
          candidateFields = value as Record<string, string>;
          break;
        }
      }

      if (candidateUrl && candidateFields) {
        return { url: candidateUrl, fields: candidateFields };
      }

      for (const value of Object.values(record)) {
        const found = extractUrlAndFields(value);
        if (found) {
          return found;
        }
      }

      return undefined;
    };

    let finalUrl: string | undefined = response.url;
    let finalFields: Record<string, string> | undefined = response.fields;

    if (!finalUrl || !finalFields) {
      const found = extractUrlAndFields(response);
      if (found) {
        if (!finalUrl) {
          finalUrl = found.url;
        }
        if (!finalFields) {
          finalFields = found.fields;
        }
      }
    }

    const normalized = {
      uploadUrl: response.uploadUrl,
      postUrl: response.postUrl,
      url: finalUrl,
      fields: finalFields,
    };

    if (!normalized.uploadUrl && (!normalized.url || !normalized.fields)) {
      // Helpful debug in dev
      if (
        typeof window !== 'undefined' &&
        process.env.NODE_ENV !== 'production'
      ) {
        console.error('Unexpected presign response shape', response);
      }
    }

    return normalized;
  };

  const uploadFile = async (
    file: File,
    onProgress?: (progress: UploadProgress) => void,
    options?: { conversationId?: string; session?: string },
  ): Promise<FileUploadResult> => {
    // Step 1: Get presigned URL
    const presignedUrlResponse = await getPresignedUrl.mutateAsync({
      fileName: file.name,
      fileType: file.type || 'text/plain',
      fileSize: file.size,
      conversationId: options?.conversationId,
      session: options?.session,
    });

    // Step 2: Upload to S3 via PUT or POST depending on response
    const normalized = normalizePresign(presignedUrlResponse);
    const result = await uploadToS3.mutateAsync({
      file,
      uploadUrl: normalized.uploadUrl,
      postUrl: normalized.postUrl,
      url: normalized.url,
      fields: normalized.fields,
      fileUrl: presignedUrlResponse.fileUrl,
      fileKey: presignedUrlResponse.fileKey,
      onProgress,
    });

    return { ...result, workspacePath: presignedUrlResponse.workspacePath };
  };

  return {
    uploadFile,
    isPending: getPresignedUrl.isPending || uploadToS3.isPending,
    error: getPresignedUrl.error || uploadToS3.error,
    isSuccess: uploadToS3.isSuccess,
    reset: () => {
      getPresignedUrl.reset();
      uploadToS3.reset();
    },
  };
};

// Utility function to validate file types (images and documents)
export const validateFileType = (file: File): boolean => {
  return isSupportedFileType(file.type);
};

// Utility function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
